/**
 * Rules engine — the Laws of the Game. (§5)
 *
 * step.ts imports exactly two functions from here:
 *
 *   - `stepMatchStateMachine` runs EARLY (stage 2): drives kickoff and every
 *     restart through setup → ready → live, advances halves, and gates which
 *     inputs are legal during a restart. (§5.1)
 *
 *   - `evaluateRules` runs LAST (stage 9), on the settled post-integration
 *     state: fouls/cards/advantage and the keeper rules (§5.3, §5.5), then
 *     offside interference (§5.4), then ball-out/goals/corners/goal-kicks
 *     (§5.2).
 *
 * Order inside `evaluateRules` is defined so co-occurring events resolve the way
 * a referee would: a whistle for a foul or an offside pre-empts a ball that also
 * left the field on the same tick. Each later stage is guarded on the phase
 * still being InPlay, so once play has been stopped nothing downstream fires.
 *
 * The extra exports (`evaluateOffsidePositions`, `beginRestart`, geometry
 * helpers) are the seams ball.ts and tests build against; they are additive and
 * do not change the two-function contract step.ts depends on.
 */

import type { StepResult, WorldState } from '../types.js';
import { MatchPhase } from '../types.js';
import { evaluateFoulsAndCards } from './fouls.js';
import { evaluateOffsideInterference } from './offside.js';
import { evaluateBallOut } from './ball-out.js';

export { stepMatchStateMachine } from './fsm.js';
export {
  attackDir,
  attackGoalX,
  defendGoalX,
  beginRestart,
  beginKickoff,
  addStoppage,
  isRestartPhase,
  nearestActivePlayerOfTeam,
  placeBallForRestart,
} from './fsm.js';
export { evaluateOffsidePositions, evaluateOffsideInterference } from './offside.js';
export { evaluateBallOut } from './ball-out.js';
export { evaluateFoulsAndCards } from './fouls.js';

/**
 * Evaluate every settled-state rule for this tick. (§5)
 * Runs last in the pipeline; see the file header for ordering rationale.
 */
export function evaluateRules(state: WorldState, out: StepResult): void {
  // Fouls, cards, advantage, keeper rules. May stop play immediately.
  evaluateFoulsAndCards(state, out);

  // Offside interference — before ball-out so an offside goal is disallowed.
  if (state.match.phase === MatchPhase.InPlay) {
    evaluateOffsideInterference(state, out);
  }

  // Ball out of play, goals, corners, goal kicks.
  if (state.match.phase === MatchPhase.InPlay) {
    evaluateBallOut(state, out);
  }
}
